"use client";
import { useQueryStorage } from "@/contexts/QueryStorageContext";
import { Content } from "@/screens/Welcome/common/Content";
import { useTranslation } from "@/i18n/client";
import { useRouter, useSearchParams } from "next/navigation";
import AppShell from "@/components/AppShell";
import Link from "@/components/Link";
import { Trans } from "react-i18next";
import { Process } from "@/types/contract";
import { useMutation } from "@tanstack/react-query";
import { createRequest } from "@/requests/request/create";
import { useProcessRouter } from "@/router/useProcessRouter";
import { somethingWentWrongModalContext } from "@/modals/SomethingWentWrong";
import { SapErrorSchema } from "@/types/sap";
import { openContextModal } from "@mantine/modals";
import { AxiosError } from "axios";
import { objectToCamel } from "ts-case-convert";
import classes from "./page.module.css";

const WelcomeNewRequestPage = () => {
  const { t } = useTranslation(["pageHint", "welcome", "common", "modals"]);

  const processRouter = useProcessRouter();
  const queryParams = useQueryStorage().storedQueries;
  const searchParams = useSearchParams();
  const router = useRouter();

  if (!queryParams.process) {
    throw new Error("requiredParamNotProvided");
  }

  if (queryParams.process.value === Process.PS && !queryParams.commodity && !queryParams.source) {
    throw new Error("requiredParamNotProvided");
  }

  const createRequestMutation = useMutation({
    mutationFn: createRequest,
    onSuccess: data => {
      const nextUrl = processRouter.getNextUrl(data.contract);
      if (nextUrl) {
        router.replace(nextUrl);
      }
    },
    onError: (e: Error) => {
      if (e instanceof AxiosError) {
        const parsedError = SapErrorSchema.safeParse(e.response?.data);
        if (parsedError.success) {
          const sapErrors = objectToCamel(parsedError.data.errors);
          const errorMessage = sapErrors[0]?.text;

          if (errorMessage) {
            return openContextModal({
              modal: "somethingWentWrong",
              innerProps: {
                title: t("common:somethingWentWrong"),
                description: errorMessage,
                closeLabel: t("modals:close"),
              },
            });
          }
        }
      }

      openContextModal(somethingWentWrongModalContext);
    },
  });

  const handleStart = () => {
    if (queryParams.process?.value === Process.PS && queryParams.pod && queryParams.bpId) {
      createRequestMutation.mutate({
        process: Process.PS,
        internalBpId: queryParams.bpId,
        pod: queryParams.pod,
        processReason: queryParams.processReason,
        sellChannel: queryParams.sellChannel,
        sellChannelType: queryParams.sellChannelType,
        requestMethod: queryParams.requestMethod,
        authorId: queryParams.authorId,
        customerType: queryParams.customerType,
      });
    } else {
      router.replace(`/request/identification?${searchParams.toString()}`);
    }
  };

  return (
    <AppShell
      hintMessage={
        <Trans
          t={t}
          i18nKey="pageHint:helpStepWelcome"
          components={{
            linkCompanyCalculation: (
              <Link href="https://www.eon.cz/firemni-zakaznici/zivnostnici/chci-eon/#kalkulace" key="linkCompanyCalculation" />
            ),
          }}
        />
      }
      contentStyles={classes.appShell}>
      <Content
        selfService
        commodity={queryParams.commodity!}
        process={queryParams.process}
        customerType={queryParams.customerType}
        processReason={queryParams.processReason}
        isLoading={createRequestMutation.isPending}
        onStart={handleStart}
      />
    </AppShell>
  );
};

export default WelcomeNewRequestPage;
